import { action, computed, makeObservable, observable } from 'mobx'

import {
  IPermitTypeChecklist,
  IPermitTypeField,
  ITypeFieldFilterOptions,
  LocationType,
  OperatorType,
  WorkflowsFilterType,
} from '~/client/graph/types.generated'
import { locationFilterPropMap } from '~/client/src/shared/constants/FilterPropertyMaps/LocationFilterPropMap'
import {
  getAllowedTemplateIds,
  resourceFilterPropMap,
} from '~/client/src/shared/constants/FilterPropertyMaps/ResourceFilterPropMap'
import {
  getAllowedWorkflowTypes,
  workflowFilterPropMap,
} from '~/client/src/shared/constants/FilterPropertyMaps/WorkflowFilterPropMap'
import locationTypesList from '~/client/src/shared/constants/locationTypesList'
import {
  equipmentPermitFieldTypes,
  locationPermitFieldTypes,
} from '~/client/src/shared/constants/PermitFieldTypeConstants'
import { MAX_SELECT_OPTIONS_COUNT } from '~/client/src/shared/constants/permitTypeFieldsConstants'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import {
  areObjectArraysEqual,
  copyObject,
} from '~/client/src/shared/utils/util'

export enum FieldState {
  Mandatory = 'mandatory',
  Optional = 'optional',
  Hidden = 'hidden',
}

export enum FieldConfiguratorTab {
  SelectionSettings = 'selection-settings',
  DynamicFilters = 'dynamic-filters',
  DependentFields = 'dependent-fields',
  AssociatedFields = 'associated-fields',
  CardView = 'card-view',
}

export function getFieldStateTranslate(value: FieldState) {
  switch (value) {
    case FieldState.Hidden:
      return Localization.translator.fieldStates.hidden
    case FieldState.Mandatory:
      return Localization.translator.fieldStates.mandatory
    case FieldState.Optional:
      return Localization.translator.fieldStates.optional
    default:
      throw new Error(value + ' is unhandled')
  }
}

type FieldSettings = { isMandatory?: boolean; isShown?: boolean }

export default class FieldConfiguratorStore {
  public static getStateFromSettings({
    isMandatory,
    isShown,
  }: FieldSettings): FieldState {
    switch (true) {
      case isMandatory:
        return FieldState.Mandatory
      case isShown:
        return FieldState.Optional

      default:
        return FieldState.Hidden
    }
  }

  public static getSettingsFromState(fieldState: FieldState): FieldSettings {
    let isMandatory = false
    let isShown = false

    switch (fieldState) {
      case FieldState.Mandatory:
        isMandatory = true
      // eslint-disable-next-line no-fallthrough
      case FieldState.Optional:
        isShown = true
    }

    return { isShown, isMandatory }
  }

  @observable public field: IPermitTypeField = null
  @observable public newSelectOption: string = ''
  @observable public activeTab: FieldConfiguratorTab =
    FieldConfiguratorTab.SelectionSettings

  public constructor(
    field: IPermitTypeField,
    private onChange: (field: IPermitTypeField) => void,
  ) {
    this.setField(field)
    makeObservable(this)
  }

  public get fieldState(): FieldState {
    return FieldConfiguratorStore.getStateFromSettings(this.field)
  }

  public get isAddSelectOptionShown(): boolean {
    return (this.field.selectOptions || []).length < MAX_SELECT_OPTIONS_COUNT
  }

  public get isMultipleDisabled(): boolean {
    return !this.field.canEditIsMandatory && !this.field.canEditIsShown
  }

  public get isEquipmentField(): boolean {
    return equipmentPermitFieldTypes.includes(this.field.type)
  }

  @computed
  public get restrictedLocationTypes(): LocationType[] {
    if (!locationPermitFieldTypes.includes(this.field.type)) {
      return null
    }

    if (equipmentPermitFieldTypes.includes(this.field.type)) {
      return locationTypesList.filter(
        t => t !== LocationType.OffloadingEquipment,
      )
    }

    return locationTypesList.filter(
      t =>
        t === LocationType.OffloadingEquipment ||
        (this.allowedLocationTypes?.length &&
          !this.allowedLocationTypes.includes(t)),
    )
  }

  // Use only for dynamic filters, will be reworked in the future
  @computed
  public get restrictedLocIdsForDynamicFilters(): string[] {
    return (
      this.field.filterOptions?.find(
        f =>
          f.propertyPath === locationFilterPropMap.ID &&
          f.operator === OperatorType.NotIn &&
          !f.fieldId,
      )?.values || []
    )
  }

  @computed
  public get allowedLocationTypes(): LocationType[] {
    return (
      (this.field.filterOptions?.find(
        f =>
          f.propertyPath === locationFilterPropMap.TYPE &&
          f.operator === OperatorType.In &&
          !f.fieldId,
      )?.values as LocationType[]) || []
    )
  }

  @computed
  public get allowedWorkflowTypes(): string[] {
    return getAllowedWorkflowTypes(this.field.filterOptions)
  }

  @computed
  public get allowedTemplateIds(): string[] {
    return getAllowedTemplateIds(this.field.filterOptions)
  }

  @computed
  public get existingDynamicFilters(): ITypeFieldFilterOptions[] {
    return this.field.filterOptions?.filter(f => !!f.fieldId) || []
  }

  @action.bound
  public setField(field: IPermitTypeField) {
    this.field = copyObject(field)
    this.updateSelectOption('')
  }

  @action.bound
  public setOnChangeHandler(onChange: (field: IPermitTypeField) => void) {
    this.onChange = onChange
  }

  @action.bound
  public setActiveTab(tab: FieldConfiguratorTab) {
    this.activeTab = tab
  }

  @action.bound
  public changeFieldState(newFieldState: FieldState) {
    if (this.field.canEditIsMandatory || this.field.canEditIsShown) {
      this.onChange(
        Object.assign(
          this.field,
          FieldConfiguratorStore.getSettingsFromState(newFieldState),
        ),
      )
    }
  }

  @action.bound
  public updateCaption(newCaption: string) {
    if (!newCaption || this.field.caption === newCaption) {
      return
    }

    this.field.caption = newCaption

    this.onChange(this.field)
  }

  @action.bound
  public updateSelectOption(newValue: string) {
    this.newSelectOption = newValue
  }

  @action.bound
  public addNewSelectOption() {
    if (!this.isAddSelectOptionShown || !this.newSelectOption?.trim()) {
      return
    }

    if (!this.field.selectOptions) {
      this.field.selectOptions = []
    }

    const trimmedValue = this.newSelectOption.trim()

    if (!this.field.selectOptions.includes(trimmedValue)) {
      this.field.selectOptions.push(trimmedValue)

      this.onChange(this.field)
    }
  }

  @action.bound
  public removeSelectOption(index: number) {
    this.field.selectOptions?.splice(index, 1)
    this.onChange(this.field)
  }

  @action.bound
  public toggleIsMultipleValue() {
    if (!this.isMultipleDisabled) {
      this.field.isMultiple = !this.field.isMultiple

      this.onChange(this.field)
    }
  }

  @action.bound
  public toggleAllowPreciseLocation() {
    this.field.allowPreciseLocation = !this.field.allowPreciseLocation

    this.onChange(this.field)
  }

  @action.bound
  public toggleUserStampEnabled() {
    this.field.isUserStampEnabled = !this.field.isUserStampEnabled

    this.onChange(this.field)
  }

  @action.bound
  public toggleAllowSelectExisting() {
    this.field.allowSelectExisting = !this.field.allowSelectExisting
    if (!this.field.allowSelectExisting && !this.field.allowCreateNew) {
      this.field.allowCreateNew = true
    }
    this.onChange(this.field)
  }

  @action.bound
  public toggleAllowCreateNew() {
    this.field.allowCreateNew = !this.field.allowCreateNew
    if (!this.field.allowCreateNew && !this.field.allowSelectExisting) {
      this.field.allowSelectExisting = true
    }
    this.onChange(this.field)
  }

  @action.bound
  public updateAllowedWorkflowTypes(types: string[]) {
    this.field.filterOptions =
      this.field.filterOptions?.filter(
        f =>
          f.propertyPath !== workflowFilterPropMap.TYPE ||
          (!!f.fieldId && !!f.fieldValues?.length),
      ) || []

    if (types.length) {
      this.field.filterOptions.push({
        propertyPath: workflowFilterPropMap.TYPE,
        operator: OperatorType.In,
        values: types,
      })
    }

    this.updateDefaultWorkflowTypeFilter(types)

    this.onChange(this.field)
  }

  private updateDefaultWorkflowTypeFilter(types: string[]) {
    if (!this.field.defaultOptions) {
      this.field.defaultOptions = {}
    }

    // Preserve other filter categories (status, company, etc.)
    const otherFilters =
      this.field.defaultOptions.filterOptions?.filter(
        f => f?.type !== WorkflowsFilterType.Type,
      ) ?? []

    if (types.length) {
      // Set allowedTypes as the Type filter, combined with other filters
      this.field.defaultOptions.filterOptions = [
        ...otherFilters,
        {
          type: WorkflowsFilterType.Type,
          values: types,
        },
      ]
    } else {
      // Clear Type filter, keep other filters
      this.field.defaultOptions.filterOptions = otherFilters.length
        ? otherFilters
        : null
    }
  }

  @action.bound
  public updateAllowedTemplateIds(templateIds: string[]) {
    this.field.filterOptions =
      this.field.filterOptions?.filter(
        f => f.propertyPath !== resourceFilterPropMap.TEMPLATE_ID,
      ) || []

    if (templateIds.length) {
      this.field.filterOptions.push({
        propertyPath: resourceFilterPropMap.TEMPLATE_ID,
        operator: OperatorType.In,
        values: templateIds,
      })
    }

    this.onChange(this.field)
  }

  @action.bound
  public updateRestrictedIdsFilters(updatedFilters: ITypeFieldFilterOptions[]) {
    this.resetIdFilters()

    this.field.filterOptions.push(...updatedFilters)

    this.onChange(this.field)
  }

  @action.bound
  public updateChecklist(checklist: IPermitTypeChecklist) {
    if (this.isChecklistChanged(this.field.checklist, checklist)) {
      this.field.checklist = checklist

      this.onChange(this.field)
    }
  }

  @action.bound
  public updateLBSLogic(isLBSLogicEnabled: boolean) {
    if (!this.field.isLBSLogicEnabled) {
      this.resetIdFilters()
    }
    this.field.isLBSLogicEnabled = isLBSLogicEnabled

    this.onChange(this.field)
  }

  @action.bound
  public updateDynamicFilters(filters: ITypeFieldFilterOptions[]) {
    this.resetDynamicFilters()

    this.field.filterOptions.push(...filters)
    this.onChange(this.field)
  }

  @action.bound
  private resetIdFilters() {
    this.field.filterOptions =
      this.field.filterOptions?.filter(
        f =>
          f.propertyPath !== locationFilterPropMap.ID ||
          (!!f.fieldId && !!f.fieldValues.length),
      ) || []
  }

  @action.bound
  private resetDynamicFilters() {
    this.field.filterOptions =
      this.field.filterOptions?.filter(f => !f.fieldId) || []
  }

  private isChecklistChanged = (
    existing: IPermitTypeChecklist,
    newChecklist: IPermitTypeChecklist,
  ): boolean => {
    return (
      !areObjectArraysEqual(existing?.list || [], newChecklist?.list || []) ||
      existing.listType !== newChecklist.listType
    )
  }
}
